import type { SelectOption } from '@common/types/templateFormFields';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import type { ChangeEvent, DragEvent } from 'react';
import { useRef } from 'react';
import { theme } from '@/theme';
import type { OptionEditorFieldData, OptionErrorType } from './types';

interface SelectFieldProps {
    index: number;
    option: SelectOption;
    onChange: (value: string) => void;
    error?: OptionErrorType;
    onRemove: () => void;
    fieldData?: OptionEditorFieldData;
    onDragStart: (index: number) => void;
    onDrop: (index: number) => void;
    fromConditional?: boolean;
    isSuperAdmin?: boolean;
}

export default function SelectField({
    fromConditional,
    isSuperAdmin,
    index,
    option,
    onChange,
    error,
    onRemove,
    fieldData,
    onDragStart,
    onDrop,
}: SelectFieldProps) {
    const draggerRef = useRef<HTMLElement>(undefined);
    const optionRef = useRef<HTMLElement>(undefined);
    const hasMatch = fieldData?.questionSchema?.conditions?.some(
        (cond) => cond.value === option.selectItemLabel,
    );
    return (
        <Box
            component={'div'}
            draggable={true}
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                background: theme.palette.neutral.white,
                padding: '4px 0',
            }}
            ref={optionRef}
            onDragLeave={(e) => {
                if (e.currentTarget.classList.contains('dropReady')) {
                    e.currentTarget.classList.remove('dropReady');
                }
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('dropReady');
            }}
            onDragStart={() => {
                onDragStart(index - 1);
            }}
            onDrop={(e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.currentTarget.classList.contains('dropReady')) {
                    e.currentTarget.classList.remove('dropReady');
                }
                onDrop(index - 1);
            }}
        >
            {/* LEFT: drag + text field */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    flex: 1,
                }}
            >
                <Box
                    component={'div'}
                    ref={draggerRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'grab',
                    }}
                    onMouseDown={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = 'grabbing';
                        }
                    }}
                    onMouseUp={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = 'grab';
                        }
                    }}
                    onMouseLeave={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = 'grab';
                        }
                    }}
                >
                    <DragIndicatorIcon sx={{ fontSize: 16 }} />
                </Box>

                <TextField
                    sx={{ width: '100%' }}
                    value={option?.selectItemLabel}
                    label={`Option ${index}`}
                    disabled={
                        hasMatch
                            ? true
                            : fromConditional || isSuperAdmin
                              ? false
                              : !!fieldData?.isLocked
                    }
                    onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        onChange(event.target.value)
                    }
                    error={error?.state}
                    helperText={error?.message || undefined}
                />
            </Box>

            {/* RIGHT: icons */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                    marginLeft: '12px',
                }}
            >
                <IconButton
                    onClick={onRemove}
                    disabled={
                        hasMatch
                            ? true
                            : fromConditional || isSuperAdmin
                              ? false
                              : !!fieldData?.isLocked
                    }
                >
                    <RemoveCircleOutlineIcon />
                </IconButton>

                {/* Reserve Info icon slot */}
                <Box sx={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
                    {!!fieldData?.questionSchema?.conditions?.length &&
                        hasMatch &&
                        !fieldData?.questionSchema?.restrictOptionEdit &&
                        !fieldData?.questionValidationSchema?.required && (
                            <Tooltip title="To edit or remove this option, please first delete the related child field(s).">
                                <IconButton>
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                </Box>
            </Box>
        </Box>
    );
}
